import { registerLocaleData } from '@angular/common';
import en from './i18n/en.json';
import fr from './i18n/fr.json';
import ar from './i18n/ar.json';
import ru from './i18n/ru.json';
import es from './i18n/es.json';
import zh from './i18n/zh.json';
import { PrimeNG } from 'primeng/config';
import { BehaviorSubject } from 'rxjs';

const i18n: Record<string, any> = {
    ar, en, es, fr, ru, zh
};
const EN = 'en';
/* add default language */
const locale_EN = {
    firstDayOfWeek: 0, // this get set directly as @Input() of calendar component
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    // today: 'Today',
    // clear: 'Clear',
    dateFormat: 'mm/dd/yy', // this get set directly as @Input() of calendar component
    weekHeader: 'Wk'
} as PrimeLocale;

export class PrimeLocale {
    static locale = new BehaviorSubject(locale_EN);
    static locales: Record<string, PrimeLocale> = {en: locale_EN};

    firstDayOfWeek: number = 0;
    dayNames: string[] = [];
    dayNamesShort: string[] = [];
    dayNamesMin: string[] = [];
    monthNames: string[] = [];
    monthNamesShort: string[] = [];
    today: string = '';
    clear: string = '';
    dateFormat: string = '';
    weekHeader: string = '';

    static getLocale(lang: string): PrimeLocale {
        return PrimeLocale.locales[lang] ? PrimeLocale.locales[lang] : PrimeLocale.locales[EN];
    }
    static initFrom(config: PrimeNG, lang: string): void {
        if (config?.setTranslation) {
            const locale = PrimeLocale.getLocale(lang);
            PrimeLocale.locale.next(locale); // this will update 1st weekday
            setTimeout(() => {
                // next cycle update labels
                config.setTranslation(locale);
            });
        } else {
            console.error('INVALID PrimeNg version, configuration:', config);
        }
    }

}

export const registerLocale = (locale: any, translations?: { [key: string]: string }) => {
    registerLocaleData(locale); // call Angular registration (works for Pipes etc)
    // first check incoming translations for buttons, then use default
    const l0 = locale[0].split('-')[0]; // leave labels from base language
    const tr = translations || i18n[l0] || { 'label.today': 'today', 'label.clear': 'clear' }; // default values if labels not provided
    // preference is for values from translation file
    PrimeLocale.locales[locale[0]] = {
        firstDayOfWeek: +locale[8],
        dayNames: tr['dayNames'] || locale[3][2],
        dayNamesShort: tr['dayNamesShort'] || locale[3][1],
        dayNamesMin: tr['dayNamesMin'] || locale[3][0],
        monthNames: tr['monthNames'] || locale[5][2] || locale[5][1] || locale[5][0],
        monthNamesShort: tr['monthNamesShort'] || locale[5][1] || locale[5][2] || locale[5][0],
        today: tr['label.today'],
        clear: tr['label.clear'],
        dateFormat: tr['dateFormat'] || locale[10][0]
    } as PrimeLocale;
};

/*
ng -> primeNg conversion
    M -> m
    MM -> mm
    MMM -> M
    MMMM -> MM
    yy -> y
    y -> yy
    yy+ -> yy // ng has several long forms
    EEEE -> DD
    EEEEEE -> D
*/
function containsExactly(f: string, c: string, times: number) {
    const s1 = Array(times + 1).join(c);
    const s2 = s1 + c;
    return f.indexOf(s1) > -1 && f.indexOf(s2) === -1;
}
export function ngToPrimeNg(f: string) {
    let f1 = f
        .replace('EEEEEE', 'D')
        .replace('EEEE', 'DD');
    // replace years
    if (containsExactly(f1, 'y', 1)) {
        // single y
        f1 = f1.replace('y', 'yy');
    } else if (containsExactly(f1, 'y', 2)) {
        // yy
        f1 = f1.replace('yy', 'y');
    } else {
        // any number of y replace with yy
        f1 = f1.replace('y*', 'yy');
    }
    // replace months
    if (containsExactly(f1, 'M', 1)) {
        f1 = f1.replace('M', 'm');
    } else if (containsExactly(f1, 'M', 2)) {
        f1 = f1.replace('MM', 'mm');
    } else {
        // remove MM
        f1 = f1.replace('MM', '');
    }
    return f1;
}

/* formats: PrimeNg
    d - day of month (no leading zero)
    dd - day of month (two digit)
    o - day of the year (no leading zeros)
    oo - day of the year (three digit)
    D - day name short
    DD - day name long
    m - month of year (no leading zero)
    mm - month of year (two digit)
    M - month name short
    MM - month name long
    y - year (two digit)
    yy - year (four digit)
*/
/* formats: ng 
    Era	G, GG & GGG	Abbreviated	AD
    GGGG	Wide	Anno Domini
    GGGGG	Narrow	A
    Year	y	Numeric: minimum digits	2, 20, 201, 2017, 20173
    yy	Numeric: 2 digits + zero padded	02, 20, 01, 17, 73
    yyy	Numeric: 3 digits + zero padded	002, 020, 201, 2017, 20173
    yyyy	Numeric: 4 digits or more + zero padded	0002, 0020, 0201, 2017, 20173
    Week-numbering year	Y	Numeric: minimum digits	2, 20, 201, 2017, 20173
    YY	Numeric: 2 digits + zero padded	02, 20, 01, 17, 73
    YYY	Numeric: 3 digits + zero padded	002, 020, 201, 2017, 20173
    YYYY	Numeric: 4 digits or more + zero padded	0002, 0020, 0201, 2017, 20173
    Month	M	Numeric: 1 digit	9, 12
    MM	Numeric: 2 digits + zero padded	09, 12
    MMM	Abbreviated	Sep
    MMMM	Wide	September
    MMMMM	Narrow	S
    Month standalone	L	Numeric: 1 digit	9, 12
    LL	Numeric: 2 digits + zero padded	09, 12
    LLL	Abbreviated	Sep
    LLLL	Wide	September
    LLLLL	Narrow	S
    Week of year	w	Numeric: minimum digits	1... 53
    ww	Numeric: 2 digits + zero padded	01... 53
    Week of month	W	Numeric: 1 digit	1... 5
    Day of month	d	Numeric: minimum digits	1
    dd	Numeric: 2 digits + zero padded	01
    Week day	E, EE & EEE	Abbreviated	Tue
    EEEE	Wide	Tuesday
    EEEEE	Narrow	T
    EEEEEE	Short	Tu
    Period	a, aa & aaa	Abbreviated	am/pm or AM/PM
    aaaa	Wide (fallback to a when missing)	ante meridiem/post meridiem
    aaaaa	Narrow	a/p
*/